import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../App.css";

const STORAGE_ACTIVE = "demoTourActive";
const STORAGE_STEP = "demoTourStep";

const steps = [
  {
    id: "workspace",
    title: "Workspace",
    body: "This is your workspace hub. Projects live here.",
    match: (path) => path === "/",
    selector: "[data-tour='workspace-header']",
  },
  {
    id: "create-project",
    title: "Create Project",
    body: "Start a new project to organize your simulations.",
    match: (path) => path === "/",
    selector: "[data-tour='create-project']",
  },
  {
    id: "select-project",
    title: "Select Project",
    body: "Open a project to manage files and run simulations.",
    match: (path) => path === "/",
    selector: "[data-tour='project-card']",
  },
  {
    id: "files-header",
    title: "Files",
    body: "Manage PDB and SDF files for docking or MD.",
    match: (path) => path.includes("/files"),
    selector: "[data-tour='files-header']",
    routeBuilder: () => buildFilesRoute(),
  },
  {
    id: "upload",
    title: "Upload",
    body: "Upload or import PDB/SDF files into your workspace.",
    match: (path) => path.includes("/files"),
    selector: "[data-tour='upload-zone']",
    routeBuilder: () => buildFilesRoute(),
  },
  {
    id: "select-files",
    title: "Select Files",
    body: "Select one PDB and one SDF for docking, or a PDB for MD.",
    match: (path) => path.includes("/files"),
    selector: "[data-tour='files-table']",
    routeBuilder: () => buildFilesRoute(),
  },
  {
    id: "viewer",
    title: "Viewer",
    body: "Preview structures in the 3D viewer.",
    match: (path) => path.includes("/files"),
    selector: "[data-tour='file-viewer']",
    routeBuilder: () => buildFilesRoute(),
  },
  {
    id: "edit",
    title: "Edit",
    body: "Open a file in the text editor for quick edits.",
    match: (path) => path.includes("/files"),
    selector: "[data-tour='edit-file']",
    routeBuilder: () => buildFilesRoute(),
  },
  {
    id: "forward-docking",
    title: "Docking",
    body: "Once a PDB and SDF are selected, continue to Docking.",
    match: (path) => path.includes("/files"),
    selector: "[data-tour='forward-docking']",
    routeBuilder: () => buildFilesRoute(),
  },
  {
    id: "docking-header",
    title: "AutoDock Vina",
    body: "This is the docking workspace for setting up and running AutoDock Vina.",
    match: (path) => path.endsWith("/docking"),
    selector: "[data-tour='docking-header']",
    routeBuilder: () => buildDockingRoute(),
  },
  {
    id: "docking-settings",
    title: "Settings",
    body: "Tune docking parameters before launching the job.",
    match: (path) => path.endsWith("/docking"),
    selector: "[data-tour='docking-parameters']",
    routeBuilder: () => buildDockingRoute(),
  },
  {
    id: "ligand-viewer",
    title: "Ligand Box",
    body: "Preview the ligand structure that will be docked.",
    match: (path) => path.endsWith("/docking"),
    selector: "[data-tour='ligand-viewer']",
    routeBuilder: () => buildDockingRoute(),
  },
  {
    id: "protein-box",
    title: "Protein Binding Box",
    body: "Adjust the binding box on the protein for docking.",
    match: (path) => path.endsWith("/docking"),
    selector: "[data-tour='protein-binding-box']",
    routeBuilder: () => buildDockingRoute(),
  },
  {
    id: "docking-console",
    title: "Console",
    body: "Status updates appear here. You can leave and return later to check progress.",
    match: (path) => path.endsWith("/docking"),
    selector: "[data-tour='docking-console-panel']",
    routeBuilder: () => buildDockingRoute(),
  },
  {
    id: "start-docking",
    title: "Start Docking",
    body: "Launch docking when everything is ready.",
    match: (path) => path.endsWith("/docking"),
    selector: "[data-tour='start-docking']",
    routeBuilder: () => buildDockingRoute(),
  },
  {
    id: "docking-results",
    title: "Docking Results",
    body: "When docking succeeds, you’ll land here to review results.",
    match: (path) => path.includes("/docking-analysis-dashboard"),
    selector: "[data-tour='docking-results-header']",
    routeBuilder: () => buildDockingResultsRoute(),
  },
  {
    id: "select-experiment",
    title: "Select Experiment",
    body: "Choose the docking experiment to inspect.",
    match: (path) => path.includes("/docking-analysis-dashboard"),
    selector: "[data-tour='docking-experiment']",
    routeBuilder: () => buildDockingResultsRoute(),
  },
  {
    id: "continue-md",
    title: "Continue with MD",
    body: "Pick a pose and continue to MD simulation.",
    match: (path) => path.includes("/docking-analysis-dashboard"),
    selector: "[data-tour='docking-simulate']",
    routeBuilder: () => buildDockingResultsRoute(),
  },
  {
    id: "md-simulation",
    title: "MD Simulation",
    body: "Configure and start your MD simulation here.",
    match: (path) => path.includes("/simulation"),
    selector: "[data-tour='md-header']",
    routeBuilder: () => buildSimulationRoute(),
  },
];

const buildFilesRoute = () => {
  const workspaceId = localStorage.getItem("demoTourWorkspaceId");
  const projectId = localStorage.getItem("demoTourProjectId");
  if (workspaceId && projectId) {
    return `/layout/${workspaceId}/${projectId}/files`;
  }
  return null;
};

const buildDockingRoute = () => {
  const workspaceId = localStorage.getItem("demoTourWorkspaceId");
  const projectId = localStorage.getItem("demoTourProjectId");
  if (workspaceId && projectId) {
    return `/layout/${workspaceId}/${projectId}/docking`;
  }
  return null;
};

const buildDockingResultsRoute = () => {
  const workspaceId = localStorage.getItem("demoTourWorkspaceId");
  const projectId = localStorage.getItem("demoTourProjectId");
  if (workspaceId && projectId) {
    return `/layout/${workspaceId}/${projectId}/docking-analysis-dashboard`;
  }
  return null;
};

const buildSimulationRoute = () => {
  const workspaceId = localStorage.getItem("demoTourWorkspaceId");
  const projectId = localStorage.getItem("demoTourProjectId");
  if (workspaceId && projectId) {
    return `/layout/${workspaceId}/${projectId}/simulation`;
  }
  return null;
};

const getStoredStep = () => {
  const value = parseInt(localStorage.getItem(STORAGE_STEP) || "0", 10);
  if (Number.isNaN(value)) return 0;
  return Math.min(Math.max(value, 0), steps.length - 1);
};

const TourOverlay = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(
    localStorage.getItem(STORAGE_ACTIVE) === "1"
  );
  const [stepIndex, setStepIndex] = useState(getStoredStep);
  const [targetRect, setTargetRect] = useState(null);
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const step = steps[stepIndex];

  useEffect(() => {
    localStorage.setItem(STORAGE_STEP, String(stepIndex));
  }, [stepIndex]);

  useEffect(() => {
    const syncFromStorage = () => {
      const storedActive = localStorage.getItem(STORAGE_ACTIVE) === "1";
      const storedStep = getStoredStep();
      setActive(storedActive);
      setStepIndex(storedStep);
    };
    syncFromStorage();
    window.addEventListener("demoTourSync", syncFromStorage);
    return () => window.removeEventListener("demoTourSync", syncFromStorage);
  }, [location.pathname]);

  useEffect(() => {
    if (!active || !step) return undefined;

    const updateRect = () => {
      if (!step.selector) {
        setTargetRect(null);
        return;
      }
      const el = document.querySelector(step.selector);
      if (!el) {
        setTargetRect(null);
        return;
      }
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    };

    updateRect();
    const handleResize = () => {
      setViewport({ width: window.innerWidth, height: window.innerHeight });
      updateRect();
    };
    const handleScroll = () => updateRect();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [active, step, location.pathname]);

  const panelStyle = useMemo(() => {
    if (!targetRect) {
      return { right: 24, bottom: 24 };
    }
    const preferredLeft = Math.min(
      targetRect.left,
      viewport.width - 360
    );
    const preferredTop = Math.min(
      targetRect.top + targetRect.height + 16,
      viewport.height - 220
    );
    return {
      left: Math.max(16, preferredLeft),
      top: Math.max(16, preferredTop),
    };
  }, [targetRect, viewport]);

  if (!active || !step) return null;

  const matchesRoute = step.match ? step.match(location.pathname) : true;
  const canNavigate = step.routeBuilder ? step.routeBuilder() : null;

  const goToStep = (index) => {
    const next = Math.min(Math.max(index, 0), steps.length - 1);
    setStepIndex(next);
  };

  const handleExit = () => {
    localStorage.removeItem(STORAGE_ACTIVE);
    localStorage.removeItem(STORAGE_STEP);
    setActive(false);
  };

  const highlightStyle = targetRect
    ? {
        top: targetRect.top - 6,
        left: targetRect.left - 6,
        width: targetRect.width + 12,
        height: targetRect.height + 12,
      }
    : null;

  return (
    <div className="sa-tour-overlay">
      {highlightStyle && matchesRoute && (
        <div className="sa-tour-highlight" style={highlightStyle} />
      )}
      <div className="sa-tour-panel" style={panelStyle}>
        <div className="sa-tour-step">
          Step {stepIndex + 1} of {steps.length}
        </div>
        <div className="sa-tour-title">{step.title}</div>
        <div className="sa-tour-body">{step.body}</div>
        {!matchesRoute && (
          <div className="sa-tour-hint">
            This step is on another page. Navigate to continue.
          </div>
        )}
        <div className="sa-tour-actions">
          <button
            type="button"
            className="sa-btn sa-btn--outline"
            onClick={handleExit}
          >
            Exit Tour
          </button>
          {canNavigate && !matchesRoute && (
            <button
              type="button"
              className="sa-btn"
              onClick={() => navigate(canNavigate)}
            >
              Go to Page
            </button>
          )}
          <div className="sa-tour-actions-right">
            <button
              type="button"
              className="sa-btn sa-btn--outline"
              onClick={() => goToStep(stepIndex - 1)}
              disabled={stepIndex === 0}
            >
              Back
            </button>
            <button
              type="button"
              className="sa-btn sa-btn--primary"
              onClick={() => {
                if (stepIndex >= steps.length - 1) {
                  handleExit();
                  return;
                }
                goToStep(stepIndex + 1);
              }}
            >
              {stepIndex >= steps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TourOverlay;
